import React from 'react'
import i18next from 'i18next'
import {ActionButtons, Status} from '@/pages/components'
import {Menu, Modal} from 'antd'
import {Teammate} from '@/stores/team-management/models'
import {TeamManagementStore} from '@/stores/team-management/TeamManagementStore'
import {ExclamationCircleOutlined} from '@ant-design/icons'
import {convertTeammateToFormModel} from '@/stores/team-management/services/processors'
import {TeammateStatus} from '@/stores/team-management/constants'
import {TagType} from '@/components/dumb/Tag/props'
import {Tag} from '@/components/dumb/Tag'
import styles from './styles.scss'
import {DELETE_INVITED_TEAMMATE, DELETE_TEAMMATE, EDIT_TEAMMATE, RESEND_INVITATION} from '@/constants/playwright-ids'

const confirmDelete = (teammateId: string, teamManagementStore: TeamManagementStore) => {
    Modal.confirm({
        icon: <ExclamationCircleOutlined />,
        content: i18next.t('team-management.confirmations.areYouSureToDeleteTeammate'),
        okText: i18next.t('team-management.confirmations.yes'),
        cancelText: i18next.t('team-management.confirmations.no'),
        onOk: () => teamManagementStore.deleteTeammate(teammateId)
    })
}

const confirmResendInvitation = (teammateId: string, teamManagementStore: TeamManagementStore) => {
    Modal.confirm({
        icon: <ExclamationCircleOutlined />,
        content: i18next.t('team-management.confirmations.areYouSureToResendInvitation'),
        okText: i18next.t('team-management.confirmations.yes'),
        cancelText: i18next.t('team-management.confirmations.no'),
        onOk: () => teamManagementStore.resendInvitation(teammateId)
    })
}

const renderActionItem = (
    label: string,
    isDisabled: boolean,
    disabledHint: string,
    onClick: () => void,
    testId: string
) => {
    if (isDisabled) {
        return (
            <div className={styles.disabledAction}>
                <div className={styles.disabledActionLabel}>{label}</div>
                <div className={styles.disabledActionHint}>{disabledHint}</div>
            </div>
        )
    }

    return (
        <a onClick={onClick} data-test-id={testId}>
            {label}
        </a>
    )
}

export const renderActionsMenu = (
    teammate: Teammate,
    teamManagementStore: TeamManagementStore,
    currentUserEmail?: string
) => {
    const isSelf = Boolean(currentUserEmail) && teammate.login?.toLowerCase() === currentUserEmail.toLowerCase()

    switch (teammate.active) {
        case true:
            return (
                <ActionButtons
                    menu={
                        <Menu
                            items={[
                                {
                                    key: '1',
                                    label: renderActionItem(
                                        i18next.t('team-management.actions.edit'),
                                        false,
                                        '',
                                        () =>
                                            teamManagementStore._editTeammateStore.openEditForm(
                                                convertTeammateToFormModel(teammate),
                                                isSelf
                                            ),
                                        EDIT_TEAMMATE
                                    )
                                },
                                {
                                    key: '2',
                                    disabled: isSelf,
                                    label: renderActionItem(
                                        i18next.t('team-management.actions.delete'),
                                        isSelf,
                                        i18next.t('team-management.actions.cannotDeleteSelf'),
                                        () => confirmDelete(teammate.id, teamManagementStore),
                                        DELETE_TEAMMATE
                                    )
                                }
                            ]}
                        />
                    }
                />
            )
        default:
            return (
                <ActionButtons
                    menu={
                        <Menu
                            items={[
                                {
                                    key: '1',
                                    disabled: isSelf,
                                    label: renderActionItem(
                                        i18next.t('team-management.actions.resendInvitation'),
                                        isSelf,
                                        i18next.t('team-management.actions.cannotResendSelf'),
                                        () => confirmResendInvitation(teammate.id, teamManagementStore),
                                        RESEND_INVITATION
                                    )
                                },
                                {
                                    key: '2',
                                    disabled: isSelf,
                                    label: renderActionItem(
                                        i18next.t('team-management.actions.delete'),
                                        isSelf,
                                        i18next.t('team-management.actions.cannotDeleteSelf'),
                                        () => confirmDelete(teammate.id, teamManagementStore),
                                        DELETE_INVITED_TEAMMATE
                                    )
                                }
                            ]}
                        />
                    }
                />
            )
    }
}

export const renderEmailWithSelfTag = (email: string, currentUserEmail?: string) => {
    const isSelf = Boolean(currentUserEmail) && email?.toLowerCase() === currentUserEmail.toLowerCase()

    if (!isSelf) {
        return email
    }

    return (
        <span className={styles.emailWithTag}>
            {email}
            <Tag type={TagType.Default} className={styles.youTag}>
                {i18next.t('team-management.you')}
            </Tag>
        </span>
    )
}

export const getTeammateStatusTag = (status: TeammateStatus) => {
    const props = {
        className: styles.status,
        iconClassName: styles.statusIcon,
        status: getTeammateStatusLabel(status)
    }

    switch (status) {
        case TeammateStatus.block:
            return <Status {...props} iconType={'close'} type={TagType.Error} />
        case TeammateStatus.invited:
            return <Status {...props} iconType={'clock'} type={TagType.Processing} />
        case TeammateStatus.ok:
            return <Status {...props} iconType={'tick'} type={TagType.Success} />
        case TeammateStatus.changePassword:
            return <Status {...props} iconType={'lock'} type={TagType.Default} />
        case TeammateStatus.validateEmail:
            return <Status {...props} iconType={'clock'} type={TagType.Processing} />
        default:
            return ''
    }
}

export const getTeammateStatusLabel = (status: TeammateStatus) => {
    switch (status) {
        case TeammateStatus.block:
            return i18next.t('team-management.statuses.block')
        case TeammateStatus.invited:
            return i18next.t('team-management.statuses.invited')
        case TeammateStatus.ok:
            return i18next.t('team-management.statuses.ok')
        case TeammateStatus.changePassword:
            return i18next.t('team-management.statuses.changePassword')
        case TeammateStatus.validateEmail:
            return i18next.t('team-management.statuses.validateEmail')
        default:
            return ''
    }
}
